/* Coffee Stage · Sharing Cup 型號頁
   ─────────────────────────────────────────────────────────────
   這個檔案不產生任何內容。頁面上看得到的每一個字、每一張圖、每一條
   連結都由 Liquid 在伺服器端寫好 —— 關掉 JavaScript，內容一個字都不少，
   釉色照樣換得到（那四格是真連結）。

   JS 只做五件事：
   1 進場遮罩揭露 —— 加 class，不改 DOM
   2 量剖面路徑長度 —— 寫一個 CSS 變數，量不到就維持完整線稿
   3 檢視鏡 —— 放大原點跟著指標（桌面）或跟著手指（觸控）
   4 圖庫縮圖 —— 攔掉會寫進歷史的錨點跳轉，改成平滑橫捲
   5 底部選型條 —— 捲過購買區之後才出現

   五件事任何一件掛掉，頁面都仍然完整可讀：橫捲是 CSS、縮圖是真錨點、
   選型條說的話在 #buy 與 #models 都有。                          */

(() => {
  "use strict";

  /* 向 section 開頭那段 inline script 報到：旗標一掛上，它就不會在 2.4 秒後
     把 .scm-js 拿掉。這個檔案沒載到＝旗標不存在＝進場遮罩整批失效，內容可見。
     放在最前面，任何後續錯誤都不影響這一句。 */
  window.__scmReady = true;

  const root = document.getElementById("cs-sc-model");
  if (!root || root.dataset.enhanced === "true") return;
  root.dataset.enhanced = "true";

  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const qsa = (sel) => Array.prototype.slice.call(root.querySelectorAll(sel));

  const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

  /* --- 第四道保險 · 叫醒圖片（只叫視窗附近的）--------------------------
     實測：分頁在背景時 loading="lazy" 完全不觸發（型號頁 25 張裡 23 張是
     lazy，只有 hero 進得來）。前三道保險救得了 opacity 與 dashoffset，
     救不了圖片 —— 圖片的載入時機不歸 CSS 管。改成 eager 就會立刻排隊下載。

     ⚠️ 2026-08-13 收窄範圍。原本是整頁全改 eager，而它同時掛在
     visibilitychange 與「換釉色成功之後」兩個地方 —— 手機切一下 App 回來、
     或者只是換個顏色，23 張圖就會一次排隊下載與解碼。實測 414：
     一次 visibilitychange，lazy 23→2。背景分頁不載圖是真 bug，
     但要補的是客人正在看的那幾張，不是整頁。 */
  const WAKE_AHEAD = 1.5;
  const WAKE_BEHIND = 0.5;
  const wakeImages = (scope) => {
    const host = scope || root;
    const vh = window.innerHeight || 800;
    const vw = window.innerWidth || 400;
    const lo = -vh * WAKE_BEHIND;
    const hi = vh * (1 + WAKE_AHEAD);
    /* 橫向也要擋：頁首圖庫是橫滑的，一整排圖的垂直位置一模一樣，
       只看上下就會把整排當成「在視窗附近」一次叫醒。 */
    const xlo = -vw * WAKE_BEHIND;
    const xhi = vw * (1 + WAKE_BEHIND);
    let n = 0;
    Array.prototype.forEach.call(host.querySelectorAll('img[loading="lazy"]'), (img) => {
      const box = img.getBoundingClientRect();
      if (box.bottom < lo || box.top > hi) return;
      if (box.right < xlo || box.left > xhi) return;
      img.loading = "eager";
      n += 1;
    });
    return n;
  };

  /* --- 讓位給主題那條 mobile dock -------------------------------------
     .scm-selbar 的 CSS 原本寫 var(--mobile-dock-height, 0px)，看起來已經
     讓過位，實測沒有：主題那個變數的值本身就是 "0px"（dock 的 CSS 走
     media="print" onload 延後載入、mobile-dock.js 是 defer，
     connectedCallback 量 offsetHeight 時量到 0），而 var() 的 fallback
     只在變數沒設時才頂上。414×896 實測重疊 51px、dock 購物車按不到。
     所以自己量，寫進 --cs-dock-h。

     量 dock 露出來多少（innerHeight − top）而不是它的高度：它平常用
     transform 收在畫面外，捲過 header 才滑上來。height 為 0 一律回 0。 */
  const dockGap = () => {
    const dock = document.querySelector(".mobile-dock");
    let gap = 0;
    if (dock) {
      const box = dock.getBoundingClientRect();
      if (box.height > 0) {
        gap = Math.min(Math.max(0, window.innerHeight - box.top), box.height);
      }
    }
    document.documentElement.style.setProperty("--cs-dock-h", Math.round(gap) + "px");
  };
  let gapTick = 0;
  const queueGap = () => {
    if (gapTick) return;
    gapTick = window.requestAnimationFrame(() => {
      gapTick = 0;
      dockGap();
    });
  };
  dockGap();
  [200, 800, 1600].forEach((t) => window.setTimeout(dockGap, t));
  window.addEventListener("scroll", queueGap, { passive: true });
  window.addEventListener("resize", queueGap);
  document.addEventListener("transitionend", (e) => {
    if (e.target && e.target.classList && e.target.classList.contains("mobile-dock")) dockGap();
  });

  /* --------------------------------------- 1 · 進場遮罩揭露 ---- */
  /* 背景分頁會把動畫時間軸凍結在起點，transition 停在 opacity:0 等於
     內容消失。計時器不受影響，1700ms 後補 .is-settled 鎖死終態 ——
     1700 是因為剖面描繪 1400ms ＋ 最深一階 221ms 的 delay ＝ 1621ms，
     鎖得比它早會把線稿砍在半路。 */
  const reveals = qsa(".scf-reveal");
  const showAll = () => {
    reveals.forEach((el) => el.classList.add("is-in", "is-settled"));
    wakeImages();
  };

  if (reduce || !("IntersectionObserver" in window)) {
    showAll();
  } else {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          e.target.classList.add("is-in");
          window.setTimeout(() => e.target.classList.add("is-settled"), 1700);
          io.unobserve(e.target);
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.02 }
    );
    reveals.forEach((el) => io.observe(el));

    /* IntersectionObserver 掛在「更新畫面」那一步上：頁面沒有被繪製時
       （背景分頁、部分內嵌瀏覽器、擷圖服務）它不會發生，內容就整段停在
       opacity:0。用一格 rAF 探測有沒有在繪製，沒有就整批揭開。 */
    let painted = false;
    requestAnimationFrame(() => {
      painted = true;
    });
    window.setTimeout(() => {
      if (!painted) showAll();
    }, 1600);
  }

  /* ------------------------ 2 · 剖面線稿描繪（--len 量測）------- */
  /* CSS 不知道一條路徑有多長。量不到就維持完整線稿，不會變成空白。 */
  const paths = qsa(".scm-p:not(.scm-p--fine)");
  paths.forEach((p) => {
    let len = 0;
    try {
      len = p.getTotalLength();
    } catch (err) {
      return;
    }
    if (len > 0) p.style.setProperty("--len", len.toFixed(1));
  });

  /* 隱藏線稿是一個要爭取的狀態，不是預設狀態。
     CSS 只在 data-scm-draw="on" 時才把 dashoffset 推到全長，而這個屬性
     由一格 rAF 掛上 —— rAF 只有在頁面真的在繪製時才回呼。背景分頁、
     bfcache 還原、in-app webview、Theme Editor 的 iframe 一律收不到，
     屬性不存在，線稿就是一開始畫好的完整圖。 */
  if (!reduce) {
    requestAnimationFrame(() => {
      root.dataset.scmDraw = "on";
    });
  }

  /* 動畫跑到一半被切到背景，時間軸凍結在中途，回來不會自己補完。
     切回可見時掃一次：還有非零 dashoffset、又沒有動畫在跑的直接歸零。
     祖先還沒 .is-in 的不動 —— 那些線本來就等著被畫。 */
  const sweepPaths = () => {
    paths.forEach((p) => {
      const host = p.closest(".scf-reveal");
      if (host && !host.classList.contains("is-in")) return;
      const off = parseFloat(getComputedStyle(p).strokeDashoffset);
      if (!off) return;
      const running =
        typeof p.getAnimations === "function" &&
        p.getAnimations().some((a) => a.playState === "running");
      if (running) return;
      p.style.strokeDasharray = "none";
      p.style.strokeDashoffset = "0";
    });
  };
  document.addEventListener("visibilitychange", () => {
    /* 兩個方向都叫醒圖片：切走的那一刻 lazy 就不再觸發，等切回來才補
       等於讓客人盯著空框。只叫視窗附近的那幾張（見 wakeImages）。 */
    wakeImages();
    if (document.hidden) return;
    window.setTimeout(sweepPaths, 120);
  });

  /* ------------------------------------------- 3 · 檢視鏡 ------- */
  /* 放大原點跟著指標走，滑到哪就湊近看哪 ——「拿起來對著光看」的手勢。
     縮放本身由 CSS 負責，這裡只餵座標。與旗艦頁同一套行為。

     以前這一段整個關在 (hover: hover) and (pointer: fine) 裡面：
     手機上一個檢視鏡都不存在。現在觸控走 click 分支 —— 點一下推近，
     再點一下收回，同時只開一個，捲出視窗自動收起。 */
  /* 🔴 存 MediaQueryList 本身，不要只存當下的 .matches（Full Review 2026-08-15）。
     原本是載入當時量一次就定案，於是：
     ・觸控筆電／接了妙控鍵盤的 iPad —— `(hover: hover) and (pointer: fine)`
       為真，只綁 pointermove 那條，手指點下去什麼都不會發生
     ・中途插拔滑鼠也不會重新評估
     現在兩條路**都綁**，各自在事件裡讀 mq.matches 決定要不要理 ——
     滑鼠裝置上 click 直接 return（桌面行為一個字都沒變），
     觸控裝置上 pointermove 直接 return。裝置變了下一次事件就跟著變。 */
  const hoverMq = window.matchMedia("(hover: hover) and (pointer: fine)");
  const hoverFine = hoverMq.matches;
  const setOrigin = (frame, event) => {
    const box = frame.getBoundingClientRect();
    if (!box.width || !box.height) return false;
    const x = clamp(((event.clientX - box.left) / box.width) * 100, 0, 100);
    const y = clamp(((event.clientY - box.top) / box.height) * 100, 0, 100);
    frame.style.setProperty("--scf-loupe-x", x.toFixed(1) + "%");
    frame.style.setProperty("--scf-loupe-y", y.toFixed(1) + "%");
    return true;
  };
  const clearOrigin = (frame) => {
    frame.style.removeProperty("--scf-loupe-x");
    frame.style.removeProperty("--scf-loupe-y");
  };

  /* 換釉色會把整條圖庫換掉，所以綁定要能重跑：只綁還沒綁過的框，
     不然同一個框會累積兩份 listener，點一下等於點兩下。 */
  let openLoupe = null;
  const closeLoupe = () => {
    if (!openLoupe) return;
    openLoupe.classList.remove("is-on");
    clearOrigin(openLoupe);
    openLoupe = null;
  };
  let loupeIo = null;
  if ("IntersectionObserver" in window) {
    loupeIo = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting && e.target === openLoupe) closeLoupe();
        });
      },
      { threshold: 0.35 }
    );
  }

  const bindLoupes = (scope) => {
    /* 🔴 換釉色會把整條圖庫換掉。原本 openLoupe 仍指著已經離開 DOM 的框，
       內部狀態認為「有一個鏡開著」而畫面上沒有；loupeIo 也從不 disconnect，
       連換五次色就累積五組已移除節點。先把兩者對齊現實。 */
    if (openLoupe && !root.contains(openLoupe)) openLoupe = null;

    const list = Array.prototype.slice.call(
      (scope || root).querySelectorAll("[data-scf-loupe]")
    );
    list.forEach((frame) => {
      if (frame.dataset.scfLoupeBound === "1") return;
      frame.dataset.scfLoupeBound = "1";

      /* 🔴 判斷依據是**這一次的指標型別**，不是裝置的 media query（重審修正）。

         `(hover: hover) and (pointer: fine)` 描述的是「主要輸入裝置」，
         不是當下這一下是誰做的。觸控筆電的主要指標是滑鼠 → mq 為真 →
         手指的 click 仍然會被擋掉；主要指標是觸控的平板接上滑鼠則相反。
         改成記住最後一次 pointerdown 的 pointerType：
         mouse 走 hover 那條、touch／pen 走點擊那條。
         mq 只留作沒有 pointerdown 可參考時的退路（例如鍵盤觸發的 click）。 */
      let lastPointer = "";
      frame.addEventListener("pointerdown", (event) => {
        lastPointer = event.pointerType || "";
      }, { passive: true });

      /* 滑鼠：滑到哪推近哪。 */
      frame.addEventListener("pointermove", (event) => {
        if (event.pointerType && event.pointerType !== "mouse") return;
        setOrigin(frame, event);
      }, { passive: true });
      frame.addEventListener("pointerleave", (event) => {
        if (event.pointerType && event.pointerType !== "mouse") return;
        clearOrigin(frame);
      });

      /* 觸控／觸控筆：點一下推近，再點收回，同時只開一個。
         滑鼠點擊直接 return —— 桌面維持「只有 hover 會推近」的既有行為。 */
      frame.addEventListener("click", (event) => {
        const byMouse = lastPointer ? lastPointer === "mouse" : hoverMq.matches;
        if (byMouse) return;
        if (frame === openLoupe) {
          closeLoupe();
          return;
        }
        closeLoupe();
        if (!setOrigin(frame, event)) return;
        frame.classList.add("is-on");
        openLoupe = frame;
      });
    });

    /* 觀察名單每次重建 —— 與 bindShots 同一個做法。只綁一次的是 listener，
       observer 觀察的是節點，節點換了就要重來。 */
    if (loupeIo) {
      loupeIo.disconnect();
      Array.prototype.forEach.call(
        root.querySelectorAll("[data-scf-loupe]"),
        (frame) => loupeIo.observe(frame)
      );
    }
  };
  bindLoupes(root);

  /* ------------------------------------- 4 · 圖庫縮圖列 --------- */
  /* 縮圖是真的 <a href="#scm-shot-n">，關掉 JS 就是原生錨點行為。
     這裡只攔兩個副作用：
     1 原生錨點會寫進瀏覽器歷史，按上一頁會變成在圖庫裡倒退
     2 不加 block:'nearest' 的話整頁會垂直彈一下，把圖庫頂到視窗頂端 */
  /* 換釉色會整條換掉這個圖庫，所以這一段做成可以重跑：舊的 observer
     先斷開，不然被換掉的節點會留在觀察名單裡，永遠回報自己在畫面外。 */
  let shotIo = null;
  const bindShots = () => {
    if (shotIo) {
      shotIo.disconnect();
      shotIo = null;
    }
    const shotsRail = root.querySelector(".scm-shots");
    const shotFigs = Array.prototype.slice.call(root.querySelectorAll(".scm-shots > figure"));
    const shotNav = Array.prototype.slice.call(root.querySelectorAll(".scm-shots-nav a[data-scm-shot]"));

    /* is-on 只是顏色。只用鍵盤或讀屏器的人看不到顏色，所以同一個狀態
       要再用 aria-current 說一次 —— 兩者一定同進同出，不可以只改一邊。 */
    const markShot = (a, on) => {
      a.classList.toggle("is-on", on);
      if (on) a.setAttribute("aria-current", "true");
      else a.removeAttribute("aria-current");
    };

    shotNav.forEach((a, i) => {
      markShot(a, i === 0);
      if (a.dataset.scmShotBound === "1") return;
      a.dataset.scmShotBound = "1";
      a.addEventListener("click", (event) => {
        const id = a.getAttribute("href") || "";
        const target = id.charAt(0) === "#" ? root.querySelector(id) : null;
        if (!target) return; /* 找不到就交回原生錨點，不要把人卡在原地 */
        event.preventDefault();
        target.scrollIntoView({ behavior: reduce ? "auto" : "smooth", inline: "start", block: "nearest" });
      });
    });

    /* ── 前一張／下一張 ＋ 計數（2026-08-19）───────────────────────────
       店主：桌面圖庫「就像產品圖片分解了一樣」。桌面原本每一格只佔 92%，
       右邊固定露出下一張的 40px 直條當提示；改成一次一整張之後，
       「還有下一張」要由明確的鍵來講（店主選「一次一整張 ＋ 箭頭鍵」）。

       🔴 這一組節點在 .scm-shots-wrap 之內，換釉色時會連整塊一起被換掉，
       所以每次 bindShots() 都要重新查一次，不能快取在外面。
       綁定用 dataset 旗標防重複，跟上面縮圖那段同一套寫法。
       ⚠️ 沒有 IntersectionObserver 就不解除 hidden —— 按鈕能捲，但
       「現在第幾張」會停在 1，一個會說謊的計數比沒有計數更糟。 */
    const shotsIo = root.querySelector("[data-scm-shots-io]");
    const shotPrev = root.querySelector("[data-scm-shot-prev]");
    const shotNext = root.querySelector("[data-scm-shot-next]");
    const shotIdxEl = root.querySelector("[data-scm-shots-i]");
    const shotNumEl = root.querySelector("[data-scm-shots-n]");
    let shotIdx = 0;

    const goShot = (i) => {
      const fig = shotFigs[Math.max(0, Math.min(i, shotFigs.length - 1))];
      if (!fig) return;
      fig.scrollIntoView({ behavior: reduce ? "auto" : "smooth", inline: "start", block: "nearest" });
    };

    const syncShotIo = (i) => {
      shotIdx = i;
      if (shotIdxEl) shotIdxEl.textContent = String(i + 1);
      if (shotPrev) shotPrev.disabled = i <= 0;
      if (shotNext) shotNext.disabled = i >= shotFigs.length - 1;
    };

    if (shotPrev && shotPrev.dataset.scmShotBound !== "1") {
      shotPrev.dataset.scmShotBound = "1";
      shotPrev.addEventListener("click", () => goShot(shotIdx - 1));
    }
    if (shotNext && shotNext.dataset.scmShotBound !== "1") {
      shotNext.dataset.scmShotBound = "1";
      shotNext.addEventListener("click", () => goShot(shotIdx + 1));
    }
    if (shotNumEl) shotNumEl.textContent = String(shotFigs.length);

    if (shotsRail && shotFigs.length && shotNav.length && "IntersectionObserver" in window) {
      if (shotsIo && shotFigs.length > 1) shotsIo.hidden = false;
      syncShotIo(0);
      /* 🔴 用 intersectionRatio 挑「露出最多的那一格」，不要用 isIntersecting
         當門檻（Full Review 2026-08-15）。

         `isIntersecting` 的意思是「有沒有重疊」，不是「有沒有過 threshold」。
         往回滑的時候，第 2 格向上穿過 0.6、第 3 格向下穿過 0.6，兩格都還
         重疊著軌道 —— 兩筆 entry 都會通過 `if (!e.isIntersecting) return`，
         誰後被處理誰贏，於是高亮可能停在**剛剛離開**的那一格。
         改成把每一格的比例記下來，每次都選最大的那一格，
         結果與 entry 的先後順序無關。 */
      const ratios = new WeakMap();
      shotIo = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => ratios.set(e.target, e.intersectionRatio));
          let best = 0;
          let bi = -1;
          shotFigs.forEach((f, i) => {
            const r = ratios.get(f) || 0;
            if (r > best) {
              best = r;
              bi = i;
            }
          });
          if (bi < 0) return;
          shotNav.forEach((a, k) => markShot(a, k === bi));
          syncShotIo(bi);

          /* 縮圖列自己也是一個 overflow-x 的捲動容器 —— 被高亮的那一格
             可能落在它的可視範圍外，客人看不到「現在在哪一格」。
             只動這一條的 scrollLeft，不用 scrollIntoView（那會捲整頁）。 */
          const nav = shotNav[bi];
          const navWrap = nav && nav.closest ? nav.closest(".scm-shots-nav") : null;
          if (!navWrap) return;
          const over = navWrap.scrollWidth - navWrap.clientWidth;
          if (over <= 0) return;
          const left = nav.offsetLeft - (navWrap.clientWidth - nav.offsetWidth) / 2;
          navWrap.scrollLeft = Math.max(0, Math.min(left, over));
        },
        /* 門檻要密。ratio 只在跨門檻時更新，稀疏的門檻會讓 WeakMap 留著
           舊值，兩格在門檻之間變化時高亮會慢半拍（重審抓到）。
           十一級的成本可以忽略 —— 每一格最多五六個節點。 */
        {
          root: shotsRail,
          threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1]
        }
      );
      shotFigs.forEach((f) => shotIo.observe(f));
    }
  };
  bindShots();

  /* --------------------------------------- 5 · 底部選型條 ------- */
  /* 捲過購買區之後才出現。不出現是預設狀態：這個檔案沒載到、
     IntersectionObserver 不存在、頁面沒有在繪製，它就一直不出現，
     而它說的每一句話在 #buy 與 #models 都還在。 */
  const selbar = root.querySelector("[data-scm-selbar]");
  const buySec = root.querySelector("#buy");
  if (selbar && buySec && "IntersectionObserver" in window) {
    /* 🔴 觀察的對象從 #buy 換成「加入購物車鍵」本身（Full Review 2026-08-15）。

       #buy 是**整個購買區**，實測有 1023px 高（釉色四格、釉面說明、
       出貨那一行都在裡面），而按鈕在它最底下。以 #buy 當門檻的意思是：
       「只要購買區還有一角在畫面上，就當作客人看得到購買鍵」——
       實測不成立：
         375×812 → 按鈕 top=965，摺線下 153px
         414×896 → 按鈕 top=975，摺線下 79px（店主的手機）
       這兩個寬度的**第一屏兩個 CTA 都看不到**：真按鈕在摺線下，
       而選型條因為 #buy 還在畫面上所以是隱藏的。
       改成觀察按鈕：按鈕不在畫面上就給那條，不分捲過頭還是還沒捲到。

       ⚠️ 沒有按鈕時（show_product_form 關掉）退回原本的 #buy 判斷 ——
       那種頁面本來就不成交，維持既有行為就好。 */
    const buyBtn = root.querySelector("#scm-form button[name='add']");
    const target = buyBtn || buySec;

    /* 🔴 捲到頁尾要收起來。原本 is-on 掛上就不再拿掉，實測在
       414×896 的頁面最底端，選型條（51px）＋ 主題的 .mobile-dock（60px）
       合計蓋住視窗底部 111px —— 蓋的是主題頁尾，而頁尾只為 dock 留了高度，
       沒有為這條再留。桌面 1280 也蓋掉 55px。
       這條說的每一句話在 #buy、#models 與正上方的 #more 都有，
       客人捲到頁尾時收起它，零損失。 */
    let offscreen = false;
    let atFoot = false;
    const sync = () => selbar.classList.toggle("is-on", offscreen && !atFoot);

    const bio = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (buyBtn) offscreen = !e.isIntersecting;
          else offscreen = !e.isIntersecting && e.boundingClientRect.top < 0;
        });
        sync();
      },
      { threshold: 0 }
    );
    bio.observe(target);

    const foot = document.querySelector("footer, .footer");
    if (foot) {
      const fio = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            atFoot = e.isIntersecting;
          });
          sync();
        },
        { threshold: 0 }
      );
      fio.observe(foot);
    }
  }

  /* ----------------------------------- 6 · 釉色就地換圖 --------- */
  /* 四格釉色本來就是真連結，而且必須繼續是真連結：關掉 JavaScript 要換得到、
     每一色要有自己可以分享的網址、瀏覽器返回鍵要回得去。所以這裡不改 DOM
     結構、不換成 <button>，只攔掉「整頁重載」這一個副作用 ——
     這一頁大約 6000px 高，重載會把 scrollY 歸零，客人得自己捲回原位。

     換上來的 HTML 是伺服器算的，不是前端拼的：
     用 Shopify 的 section rendering（?section_id=）只取這一個 section，
     所以圖庫去重、燼金黑那一格的 asset 覆寫、售罄與價格，全部跟整頁重載
     算出來的一模一樣，前端沒有第二套邏輯可以跟它不一致。

     任何一環缺席就整段不啟用，連結回到原生行為：
     fetch / history.pushState / DOMParser 少一個、量不到 section id、
     抓回來的東西解不出想要的節點 —— 一律讓瀏覽器自己去跳。 */
  (() => {
    const glazeBox = root.querySelector("[data-scm-glaze]");
    if (!glazeBox) return;

    /* 首次載入也沿用切換釉色時的同一個名稱來源：伺服器已用
       aria-current／is-on 標出真正選中的 Variant，名稱直接取該卡片，
       避免主要語言與翻譯語言各自再猜一次選項字串。 */
    const initialLabel = glazeBox.querySelector(".scm-glaze-current");
    const initialName = glazeBox.querySelector(".scm-glaze-cell.is-on .scm-glaze-name");
    if (initialLabel && initialName) initialLabel.textContent = initialName.textContent;

    if (!window.fetch || !window.DOMParser || !window.history || !history.pushState) return;

    const host = root.closest ? root.closest(".shopify-section") : null;
    const sectionId = host && host.id ? host.id.replace(/^shopify-section-/, "") : "";
    if (!sectionId) return;

    /* 整塊換掉的：圖庫（主圖跟著釉色走，而且要重跑去重）、價格、四格本身。
       表單刻意不換 —— 換掉會把客人剛填的數量清掉，而且 <form> 上可能掛著
       主題自己的東西。只改它裡面那個 variant id 與按鈕的兩個狀態。 */
    const SWAP = [".scm-shots-wrap", ".scm-price", "[data-scm-glaze]", ".scm-selbar-p"];

    const strip = (u) => {
      const url = new URL(u, location.href);
      url.hash = "";
      return url.href;
    };

    const cache = new Map();
    const load = (url) => {
      const key = strip(url);
      if (cache.has(key)) return cache.get(key);
      const api = new URL(key);
      api.searchParams.set("section_id", sectionId);
      const p = fetch(api.href, { headers: { "X-Requested-With": "XMLHttpRequest" } })
        .then((r) => (r.ok ? r.text() : null))
        .catch(() => null)
        .then((html) => {
          /* 失敗不留在快取裡，下一次重試 */
          if (!html) cache.delete(key);
          return html;
        });
      cache.set(key, p);
      return p;
    };

    const apply = (html) => {
      let doc;
      try {
        doc = new DOMParser().parseFromString(html, "text/html");
      } catch (err) {
        return false;
      }
      if (!doc || !doc.querySelector("[data-scm-glaze]")) return false;

      /* 🔴 fail closed：先全部找齊，才動第一個節點。

         原本是 `SWAP.forEach(sel => { … if (!next || !cur) return; … })`。
         那個 `return` 只跳出**這一次 callback**，迴圈照樣往下跑 —— 於是
         「圖庫換了、價格沒換」這種局部狀態會留在畫面上，而下面的
         variant id 照樣更新成新釉色的。**客人看到新顏色配舊價格，
         按下去買到的是新顏色。**

         任何一格缺席就整段放棄、回傳 false —— go() 會接手做
         `location.assign()` 整頁重載，那條路算出來的一定是對的。
         這正是本檔開頭那句「任何一環缺席就整段不啟用」的意思，
         只是原本漏在這一層沒有落實。 */
      const pairs = [];
      for (let i = 0; i < SWAP.length; i += 1) {
        const next = doc.querySelector(SWAP[i]);
        const cur = root.querySelector(SWAP[i]);
        if (!next || !cur) return false;
        pairs.push([cur, next]);
      }

      /* 表單：只搬 variant id 與按鈕狀態，不動 <form> 本身。
         ⚠️ 只在**這一頁本來就有表單**時才要求對面也有 —— `show_product_form`
         關掉時整個 <form> 不存在，那種頁面根本不成交，沒有東西會不同步。
         但只要這一頁有表單，換不到新的 variant id 就是同一種病：
         畫面換了、買到的是舊的 —— 一樣要整頁重載。 */
      const idInput = root.querySelector("#scm-form input[name='id']");
      const nextId = doc.querySelector("#scm-form input[name='id']");
      if (idInput && !nextId) return false;

      /* 🔴 按鈕的狀態也要在 fail closed 的範圍內（重審補的）。
         第一版把 btn／nextBtn 留在替換之後才查，任一缺席就「跳過同步」——
         結果是圖片、價格、variant id 都換成新釉色了，按鈕卻還掛著
         **上一個釉色**的售罄狀態與字。缺貨與否是會影響按不按得下去的，
         跟價格同一個等級。缺就整頁重載。 */
      const btn = root.querySelector("#scm-form button[name='add']");
      const nextBtn = doc.querySelector("#scm-form button[name='add']");
      if (btn && !nextBtn) return false;

      /* 底部選型條那顆也是真的加入購物車（form="scm-form"），狀態要一起同步。
         它在 <form> 外面，所以上面那個 #scm-form 選擇器抓不到它。
         同一個 fail closed 標準：這一頁有、對面沒有 → 整頁重載。 */
      const sbBtn = root.querySelector(".scm-selbar-cta[name='add']");
      const nextSbBtn = doc.querySelector(".scm-selbar-cta[name='add']");
      if (sbBtn && !nextSbBtn) return false;

      /* 🔴 焦點承接。被換掉的節點如果正握著焦點，`replaceWith` 之後那個
         元素連同焦點一起離開 DOM，焦點掉回 <body> —— 鍵盤使用者每換一次
         釉色就被丟回頁首，下一個 Tab 從麵包屑重新開始。
         先記住焦點在不在這幾塊裡面，換完接到新的那一格上。 */
      const active = document.activeElement;
      const hadFocus = pairs.some((pair) => pair[0].contains(active));

      const fresh = pairs.map((pair) => {
        /* importNode 而不是直接搬：跨 document 的節點在部分引擎上會出事 */
        const node = document.importNode(pair[1], true);
        pair[0].replaceWith(node);
        return node;
      });

      if (hadFocus) {
        const box = root.querySelector("[data-scm-glaze]");
        const back =
          (box && box.querySelector("a[aria-current]")) ||
          (box && box.querySelector(".scm-glaze-cell.is-on a")) ||
          box;
        /* preventScroll：接回焦點不應該順便把頁面捲走 */
        if (back && back.focus) back.focus({ preventScroll: true });
      }

      if (idInput && nextId) idInput.value = nextId.value;
      /* 🔴 只寫進 <span>，不要動按鈕本身的 textContent。
         那個 <span> 是 <product-form> 的契約：它的 constructor 抓
         `submitButton.querySelector('span')` 存成 submitButtonText，
         售罄分支會對它做 classList。用 textContent 賦值會把整個 span
         換成一個文字節點 —— 換一次釉色，product-form 手上那個參照就
         指向一棵已經離開 DOM 的樹，之後的售罄提示對著空氣做。
         沒有 span 的舊結構（或設定關掉一鍵付款時）退回原本的寫法。 */
      const syncBtn = (a, b) => {
        if (!a || !b) return;
        a.disabled = b.disabled;
        const lab = a.querySelector("span");
        const nextLab = b.querySelector("span");
        if (lab && nextLab) lab.textContent = nextLab.textContent;
        else a.textContent = b.textContent;
      };
      syncBtn(btn, nextBtn);
      syncBtn(sbBtn, nextSbBtn);

      /* 換進來的東西一律直接就位。「還沒進場」是一個要爭取的狀態，
         不是預設狀態 —— 剛換進來的節點已經在畫面上，而且 §1 那個
         IntersectionObserver 只觀察開頁當時的那一批，新節點沒有人會叫醒它，
         不就位就會永遠停在 opacity:0。

         🔴 範圍只能是**剛換進來的那幾棵子樹**。原本這裡掃的是
         `root.querySelectorAll(".scf-reveal")` —— 整份文件、不分遠近，
         於是在購買區換一次釉色，底下 #models／#design／#specs／#craft
         一路到頁尾十幾個區塊全部被判定成「已完成進場」，客人往下捲
         再也看不到任何進場，剖面線稿也早就畫完了。 */
      const settle = (node) => {
        if (node.classList && node.classList.contains("scf-reveal")) {
          node.classList.add("is-in", "is-settled");
        }
        if (!node.querySelectorAll) return;
        Array.prototype.forEach.call(node.querySelectorAll(".scf-reveal"), (el) => {
          el.classList.add("is-in", "is-settled");
        });
      };
      fresh.forEach(settle);
      wakeImages();
      bindLoupes(root);
      bindShots();
      /* 🔴 這裡原本 dispatch 一個 "cs-sc-model:applied"，唯一的聽眾是
         對照表的重捲。但對照表**不在 SWAP 清單裡**、也不在那四塊底下 ——
         換釉色時它一個節點都沒有動，重捲只會把客人自己橫捲過的位置推回去。
         事件與監聽一起移除（見 §7 對照表那一段的說明）。 */
      return true;
    };

    let current = strip(location.href);
    let seq = 0;
    /* 每次都重新問一次是誰：換過一輪之後，開頭抓到的那個節點已經被換掉了 */
    /* 🔴 換釉色期間必須擋住「加入購物車」。
       點下去的那一刻畫面就換色了（樂觀更新，不必等網路），但表單裡的
       <input name="id"> 要等 section 抓回來、apply() 寫進去才是新 variant。
       中間那段時間按下去，送出的是**上一個釉色**的 variant id —— 網路慢
       一點就會發生，客人收到的顏色跟他選的不同。

       用 data 屬性 ＋ submit 攔截，不動按鈕的 disabled：
       apply() 會依伺服器版本設 disabled（可能是缺貨），那是它的職責，
       這裡去改會把它的判斷蓋掉。#scm-form 不在 SWAP 清單裡，節點不會
       被替換，所以監聽掛一次就夠。 */
    const busy = (on) => {
      const box = root.querySelector("[data-scm-glaze]");
      if (box) {
        if (on) box.setAttribute("aria-busy", "true");
        else box.removeAttribute("aria-busy");
      }
      const form = root.querySelector("#scm-form");
      if (!form) return;
      if (on) form.setAttribute("data-scm-pending", "");
      else form.removeAttribute("data-scm-pending");
      const add = form.querySelector("button[name='add']");
      if (add) add.setAttribute("aria-disabled", on ? "true" : "false");
    };

    (() => {
      const form = root.querySelector("#scm-form");
      if (!form) return;
      form.addEventListener("submit", (e) => {
        if (form.hasAttribute("data-scm-pending")) e.preventDefault();
      });
    })();

    const go = (url, push) => {
      const key = strip(url);
      const mine = ++seq;
      busy(true);
      return load(key).then((html) => {
        if (mine !== seq) return; /* 連點兩格，只認最後一格 */
        if (!html || !apply(html)) {
          window.location.assign(key);
          return;
        }
        busy(false);
        if (push) history.pushState({ scmGlaze: key }, "", key);
        current = key;
      });
    };

    const hit = (target) =>
      target && target.closest ? target.closest(".scm-glaze-cell a[href]") : null;

    /* 先抓起來放著：手指按下去到放開之間就開始下載，放開時多半已經到了。 */
    const warm = (e) => {
      const a = hit(e.target);
      if (a) load(a.href);
    };
    root.addEventListener("pointerdown", warm, { passive: true });
    root.addEventListener("pointerover", warm, { passive: true });

    root.addEventListener("click", (e) => {
      if (e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = hit(e.target);
      if (!a) return;
      let url;
      try {
        url = new URL(a.href, location.href);
      } catch (err) {
        return;
      }
      if (url.origin !== location.origin) return;
      e.preventDefault();
      if (a.getAttribute("aria-current")) return; /* 已經是這一色，不必來回一趟 */

      /* 先把選中狀態搬過去，用的是那一格本來就印在畫面上的字 ——
         沒有新文案，也不必等網路。伺服器的版本回來時會覆蓋掉這一版。 */
      const cell = a.parentElement;
      const list = cell && cell.parentElement;
      if (list) {
        Array.prototype.forEach.call(list.children, (li) => {
          li.classList.toggle("is-on", li === cell);
          const link = li.querySelector("a");
          if (link && li !== cell) link.removeAttribute("aria-current");
        });
        a.setAttribute("aria-current", "true");
      }
      const label = root.querySelector(".scm-glaze-current");
      const name = a.querySelector(".scm-glaze-name");
      if (label && name) label.textContent = name.textContent;

      go(url.href, true);
    });

    /* 上一頁／下一頁也要走 go()。
       這裡原本自己 load().then(apply)，少了 go() 的兩道保護：
       ① busy() 的表單鎖 —— 沒有它，快速 Back→Forward 期間網址已經是 B、
          表單裡的 <input name="id"> 還是 A，按下加入購物車就送出舊釉色；
       ② seq 序列檢查 —— 沒有它，兩次回上頁的回應誰先到誰算數。
       直接點釉色那條路徑修過了，這條沒有，等於 P0 只補了一半。
       push 傳 false：瀏覽器已經幫我們改好網址了，再 pushState 會多一筆。 */
    window.addEventListener("popstate", () => {
      const next = strip(location.href);
      if (next === current) return; /* 只是錨點來回，不是換釉色 */
      go(next, false);
    });
  })();

  /* ── 五款對照表：開頁就把當前這一款的那一欄捲進畫面 ──────────────────
     表格 min-width 480px，在 414 上要橫捲 218px。Alpha 與 Beta 是第 4、5 欄，
     不捲的話開頁時整欄在畫面外 —— 而這張表整個設計理由就是
     「當前這一款用釉色底線標出來」，看不到就等於沒有標。

     ⚠️ 只動容器自己的 scrollLeft，**不用 scrollIntoView** ——
     那會連帶捲動整個頁面，客人一開頁就被丟到頁面中段。
     ⚠️ 也不用 smooth：開頁時的自動平滑捲動會被誤讀成「頁面在跑」，
     而且 prefers-reduced-motion 的人不該看到任何非他發起的動畫。 */
  const centerCurrentColumn = () => {
    const wrap = root.querySelector(".scm-cmp-wrap");
    if (!wrap) return;
    const cur = wrap.querySelector("th[scope='col'].is-on");
    if (!cur) return;
    const over = wrap.scrollWidth - wrap.clientWidth;
    if (over <= 0) return; /* 桌面不需要橫捲，不要動它 */
    const rowHead = wrap.querySelector("th[scope='row']");
    const pad = rowHead ? rowHead.getBoundingClientRect().width : 0;
    /* 讓那一欄的左緣停在固定列名的右邊，而不是被壓在下面 */
    const target = cur.offsetLeft - pad - 8;
    wrap.scrollLeft = Math.max(0, Math.min(target, over));
  };

  centerCurrentColumn();

  /* 🔴 原本這裡掛的是 `document.addEventListener("cs-sc-model:applied", …)`，
     註解寫「換釉色會重繪整個 section」—— **那是錯的**。
     SWAP 清單只有 .scm-shots-wrap／.scm-price／[data-scm-glaze]／.scm-selbar-p，
     對照表既不在裡面、也不在它們底下，換釉色時這張表一個節點都沒有動，
     選中的那一欄也還是同一款。那次重捲不但多餘，還會**把客人自己橫捲過的
     位置推回去** —— 他正在看 Alpha 那一欄，換個釉色就被拉回本款。
     監聽與 apply() 裡的 dispatch 一起移除。

     真正需要重算的是**版面變了**的時候：轉螢幕方向、視窗縮放、
     字型 swap 進來使欄寬改變。原本一次都沒掛，所以直向開頁捲好之後
     轉橫再轉回直，那一欄就不在原位了。 */
  const cmpWrap = root.querySelector(".scm-cmp-wrap");
  if (cmpWrap) {
    /* 客人自己橫捲過就不要再插手。

       🔴 第一版用 pointerdown／wheel／touchstart／keydown 判斷 —— 重審抓到
       那會被**純垂直捲頁面**誤觸：手指或滑鼠指標剛好落在這張表上，
       捲整頁就算一次；表格新加了 tabindex 之後，Tab 離開的 keydown 也算一次。
       一旦誤觸，之後轉螢幕方向就再也不會重新置中。

       改成直接量**這一條自己的 scrollLeft 有沒有離開我們寫進去的值**：
       我們每次寫都記下來，scroll 事件裡對不上就是客人捲的。
       與輸入裝置無關，垂直捲動不會改變 scrollLeft，所以不會誤判。 */
    let userScrolled = false;
    let lastAuto = cmpWrap.scrollLeft;
    cmpWrap.addEventListener("scroll", () => {
      if (Math.abs(cmpWrap.scrollLeft - lastAuto) > 2) userScrolled = true;
    }, { passive: true });

    let tick = 0;
    const recenter = () => {
      if (userScrolled) return;
      window.clearTimeout(tick);
      tick = window.setTimeout(() => {
        centerCurrentColumn();
        lastAuto = cmpWrap.scrollLeft;
      }, 120);
    };
    window.addEventListener("resize", recenter);
    window.addEventListener("orientationchange", recenter);
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(recenter).catch(() => {});
    }
  }
})();
